export const RETURN_REFUND_POLICY_VERSION = "2026-04-05";
export const RETURN_REFUND_REASON_OTHER_CODE = "OTHER_ISSUE";

export const RETURN_REFUND_REASON_OPTIONS = Object.freeze([
  {
    code: "WRONG_ITEM",
    label: "Received the wrong item",
    issueClassification: "SELLER_REPORTED",
    shippingResponsibility: "SELLER_REVIEW",
    aliases: ["WRONG ITEM", "RECEIVED WRONG ITEM"],
  },
  {
    code: "DAMAGED_ITEM",
    label: "Item arrived damaged",
    issueClassification: "SELLER_REPORTED",
    shippingResponsibility: "SELLER_REVIEW",
    aliases: ["DAMAGED ITEM", "ARRIVED DAMAGED"],
  },
  {
    code: "MISSING_PARTS",
    label: "Missing parts or accessories",
    issueClassification: "SELLER_REPORTED",
    shippingResponsibility: "SELLER_REVIEW",
    aliases: ["MISSING PARTS", "MISSING ACCESSORIES"],
  },
  {
    code: "NOT_AS_DESCRIBED",
    label: "Item does not match the description",
    issueClassification: "SELLER_REPORTED",
    shippingResponsibility: "SELLER_REVIEW",
    aliases: ["NOT AS DESCRIBED", "DOES NOT MATCH DESCRIPTION"],
  },
  {
    code: "CHANGED_MIND_WRONG_SIZE",
    label: "Changed my mind / ordered the wrong size",
    issueClassification: "SHOPPER_RESPONSIBLE",
    shippingResponsibility: "SHOPPER",
    aliases: ["CHANGED MY MIND", "WRONG SIZE", "ORDERED WRONG SIZE", "CHANGE OF MIND"],
  },
  {
    code: "QUALITY_NOT_AS_EXPECTED",
    label: "Item quality is not as expected",
    issueClassification: "REVIEW_REQUIRED",
    shippingResponsibility: "CONDITIONAL",
    aliases: ["QUALITY NOT AS EXPECTED"],
  },
  {
    code: RETURN_REFUND_REASON_OTHER_CODE,
    label: "Other issue not listed",
    issueClassification: "REVIEW_REQUIRED",
    shippingResponsibility: "CONDITIONAL",
    allowCustomReason: true,
    aliases: ["OTHER", "OTHER ISSUE", "OTHER ISSUE NOT LISTED"],
  },
]);

function normalizeToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const REASON_BY_CODE = new Map(
  RETURN_REFUND_REASON_OPTIONS.map((option) => [option.code, option])
);

const REASON_BY_ALIAS = new Map(
  RETURN_REFUND_REASON_OPTIONS.flatMap((option) => [
    [normalizeToken(option.label), option],
    ...((option.aliases || []).map((alias) => [normalizeToken(alias), option])),
  ])
);

export function getReturnRefundReasonOption(code) {
  return REASON_BY_CODE.get(normalizeToken(code)) || null;
}

export function inferReturnRefundReasonOption(reasonText) {
  const normalizedReason = normalizeToken(reasonText);
  if (!normalizedReason) return null;

  const exactMatch = REASON_BY_ALIAS.get(normalizedReason);
  if (exactMatch) return exactMatch;

  return RETURN_REFUND_REASON_OPTIONS.find((option) => (
    normalizedReason.includes(normalizeToken(option.label))
    || (option.aliases || []).some((alias) => normalizedReason.includes(normalizeToken(alias)))
  )) || null;
}

export function resolveReturnRefundReasonOption({ reasonCode = null, reasonText = null } = {}) {
  return (
    getReturnRefundReasonOption(reasonCode)
    || inferReturnRefundReasonOption(reasonText)
    || getReturnRefundReasonOption(RETURN_REFUND_REASON_OTHER_CODE)
  );
}

function cleanText(value) {
  const normalized = String(value || "").trim();
  return normalized || null;
}

function joinAddressParts(parts = []) {
  return parts
    .map((part) => cleanText(part))
    .filter(Boolean)
    .join(", ");
}

function normalizeReturnAddress(returnAddress = null) {
  if (!returnAddress) {
    return {
      isAvailable: false,
      fullAddress: null,
      lines: [],
    };
  }

  if (typeof returnAddress === "string") {
    const fullAddress = cleanText(returnAddress);
    return {
      isAvailable: Boolean(fullAddress),
      fullAddress,
      lines: fullAddress ? [fullAddress] : [],
    };
  }

  const nestedAddress = returnAddress?.address && typeof returnAddress.address === "object"
    ? returnAddress.address
    : null;
  const source = nestedAddress ? { ...returnAddress, ...nestedAddress } : returnAddress;

  const line1 = cleanText(
    source?.line1
    || source?.addressLine1
    || source?.address_line1
    || source?.streetAddress
    || source?.street_address
    || source?.street
  );
  const line2 = cleanText(joinAddressParts([
    source?.line2,
    source?.addressLine2,
    source?.address_line2,
    source?.unit,
    source?.suite,
    source?.floor,
    source?.building,
    source?.subdivision,
    source?.district,
    source?.barangay,
    source?.landmark,
  ]));
  const city = cleanText(
    source?.city
    || source?.cityMunicipality
    || source?.city_municipality
    || source?.municipality
    || source?.town
  );
  const province = cleanText(source?.province || source?.state);
  const region = cleanText(source?.region);
  const postalCode = cleanText(source?.postalCode || source?.postal_code || source?.zipCode || source?.zip_code);
  const fullAddress = cleanText(
    source?.fullAddress
    || source?.full_address
    || source?.formatted
    || (typeof returnAddress?.address === "string" ? returnAddress.address : null)
    || joinAddressParts([line1, line2, city, province, region, postalCode])
  );
  const localityLine = joinAddressParts([city, province, region, postalCode]);
  const lines = [line1, line2, localityLine].filter(Boolean);

  return {
    isAvailable: Boolean(fullAddress),
    fullAddress,
    lines: lines.length > 0 ? lines : (fullAddress ? [fullAddress] : []),
  };
}

function buildShippingNotice(shippingResponsibility) {
  switch (normalizeToken(shippingResponsibility)) {
    case "SHOPPER":
      return "This reason is usually shopper-initiated or not seller-fault, so the shopper shoulders the return shipping fee when a return shipment is required.";
    case "CONDITIONAL":
      return "If seller review finds the issue invalid, shopper-caused, or not covered by seller/platform fault, the shopper shoulders the return shipping fee.";
    case "SELLER_REVIEW":
    default:
      return "This reason is treated as a seller-side issue report. If the seller confirms it, follow the return instructions given during approval before arranging any shipment.";
  }
}

function buildBadges(issueClassification, shippingResponsibility) {
  const badges = [{ label: "Return may be required before refund", tone: "orange" }];
  const normalizedClassification = normalizeToken(issueClassification);
  const normalizedShipping = normalizeToken(shippingResponsibility);

  if (normalizedClassification === "SELLER_REPORTED") {
    badges.unshift({ label: "Seller-side issue reported", tone: "sky" });
  } else if (normalizedClassification === "SHOPPER_RESPONSIBLE") {
    badges.unshift({ label: "Shopper-paid return shipping", tone: "rose" });
  } else {
    badges.unshift({ label: "Seller review required", tone: "amber" });
  }

  if (normalizedShipping === "CONDITIONAL") {
    badges.push({ label: "Shipping fee depends on review", tone: "rose" });
  }

  return badges;
}

function buildNextSteps(normalizedStatus, referenceLabel, hasReturnAddress) {
  switch (normalizedStatus) {
    case "APPROVED":
      return [
        "Your request was approved. Follow the approved return instructions for this order.",
        hasReturnAddress
          ? "Ship the parcel to the business return address shown below once the approval note confirms the return handoff."
          : "Before shipping anything, confirm the business return address through chat if it is still missing from the instructions below.",
        `Prepare the parcel and keep ${referenceLabel} ready if the seller or courier asks for a reference.`,
        "If the seller requires the item back, ship it first. Full refund is released after the returned item is received and checked.",
      ];
    case "REJECTED":
      return [
        "Review the seller note to understand why the request was not approved.",
        `If you still need help, contact the seller through chat and mention ${referenceLabel}.`,
      ];
    case "REFUNDED":
    case "COMPLETED":
      return [
        "The refund for this request has already been processed.",
        "Keep your courier receipt or shipment proof until the refund is fully reflected in your account or wallet.",
      ];
    case "REQUESTED":
    default:
      return [
        "The seller is reviewing your request, reason, and evidence.",
        "Keep the item with you and wait for approval or return instructions before shipping anything.",
        "If the seller asks for a physical return, send it back first. Full refund is released after the returned item is received and checked.",
      ];
  }
}

function buildCourierSteps(normalizedStatus, referenceLabel, hasReturnAddress) {
  if (!["REQUESTED", "APPROVED"].includes(normalizedStatus)) {
    return [];
  }

  return [
    normalizedStatus === "APPROVED"
      ? "Follow the seller approval note or chat instructions before arranging the return shipment."
      : "Wait for seller approval or return instructions before booking the return shipment.",
    "Repack the item securely. Use the original packaging, tags, and included accessories when available.",
    `Include ${referenceLabel} on the parcel or receipt if the seller asks for a reference.`,
    hasReturnAddress
      ? "Use the business return address shown below when you book the courier pickup or drop-off."
      : "If the business return address is still missing, confirm it through chat before booking the courier.",
    "Keep the receipt and tracking number. Share them through chat if the seller asks for proof of shipment.",
    "Check My Purchases or Order Tracking for status updates until the request is completed.",
  ];
}

export function buildReturnRefundPolicyView({ policy = null, reason = null, status = null, orderReference = null, returnAddress = null } = {}) {
  const option = resolveReturnRefundReasonOption({
    reasonCode: policy?.reasonCode || policy?.reason_code || null,
    reasonText: reason,
  });
  const issueClassification = policy?.issueClassification || policy?.issue_classification || option?.issueClassification || "REVIEW_REQUIRED";
  const shippingResponsibility = policy?.shippingResponsibility || policy?.shipping_responsibility || option?.shippingResponsibility || "CONDITIONAL";
  const normalizedStatus = normalizeToken(status || "REQUESTED");
  const referenceLabel = orderReference ? `Order ID ${orderReference}` : "your order ID";
  const normalizedReturnAddress = normalizeReturnAddress(returnAddress);

  return {
    option,
    issueClassification,
    shippingResponsibility,
    badges: buildBadges(issueClassification, shippingResponsibility),
    highlights: [
      {
        title: "Item Condition",
        body: "Return items in acceptable condition. Do not intentionally damage the item. Use the original packaging when available, then pack and seal the parcel securely.",
      },
      {
        title: "Return Shipping",
        body: buildShippingNotice(shippingResponsibility),
      },
      {
        title: "Refund Release",
        body: "If seller approval requires a physical return, send the item back first. Full refund is released after the returned item is received and checked.",
      },
    ],
    nextSteps: buildNextSteps(normalizedStatus, referenceLabel, normalizedReturnAddress.isAvailable),
    courierSteps: buildCourierSteps(normalizedStatus, referenceLabel, normalizedReturnAddress.isAvailable),
    returnAddress: normalizedReturnAddress.isAvailable
      ? {
          title: "Business Return Address",
          body: "Use this business return address only after the seller approves the request and confirms the return handoff.",
          lines: normalizedReturnAddress.lines,
          isAvailable: true,
        }
      : {
          title: "Business Return Address",
          body: "A complete business return address for this order is not available yet. Wait for approval, then confirm the exact address through seller chat before shipping anything.",
          lines: [],
          isAvailable: false,
        },
    acknowledgementText: "I understand that return eligibility, shipping responsibility, and refund release follow the return and refund terms shown here.",
    policyVersion: RETURN_REFUND_POLICY_VERSION,
  };
}